// Object-key resolution helpers for selector/object variable lookups.
// Example: key "fontSize" resolves against "font-size" fallback entries.

// Resolve object key using exact, kebab-case, and camelCase fallback lookups.
export function resolveObjectKeyValue(objectMap: Record<string, string>, key: string): string | undefined {
  const normalizedKey = key.trim()
  const kebabKey = toKebabCase(normalizedKey)
  const camelKey = toCamelCase(normalizedKey)
  const candidates = [
    normalizedKey,
    kebabKey,
    camelKey,
    normalizedKey.toLowerCase(),
    kebabKey.toLowerCase(),
  ]

  // Return first matching key candidate from object variable map.
  for (const candidate of candidates) {
    if (candidate in objectMap) {
      return objectMap[candidate]
    } else {
      // Continue candidate fallback search.
    }
  }

  const target = normalizedKey.toLowerCase()
  // Fallback to case-insensitive key search as last lookup step.
  for (const [entryKey, entryValue] of Object.entries(objectMap)) {
    if (entryKey.toLowerCase() === target || toKebabCase(entryKey).toLowerCase() === target) {
      return entryValue
    } else {
      // Continue scanning entry map for fallback key match.
    }
  }

  // Return undefined when no object key candidate matches.
  return undefined
}

// Convert camelCase keys into kebab-case representation.
function toKebabCase(value: string): string {
  // Keep already-prefixed variable keys unchanged.
  if (value.startsWith('--')) {
    return value
  } else {
    // Continue kebab-case normalization for plain keys.
  }

  let output = ''
  // Insert `-` before uppercase characters and lower-case them.
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char >= 'A' && char <= 'Z') {
      output += `-${char.toLowerCase()}`
      continue
    } else {
      // Keep non-uppercase character as-is.
    }
    output += char
  }

  // Return kebab-cased key.
  return output
}

// Convert kebab-case keys into camelCase representation.
function toCamelCase(value: string): string {
  let output = ''
  // Convert `-x` segments to `X`.
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '-' && index + 1 < value.length) {
      const next = value[index + 1]
      if (next >= 'a' && next <= 'z') {
        output += next.toUpperCase()
        index += 1
        continue
      } else {
        // Keep hyphen when next char cannot be camelized.
      }
    } else {
      // Keep normal characters unchanged.
    }
    output += char
  }

  // Return camelCased key.
  return output
}
